import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogContent,
    Button,
    Typography,
    Toolbar,
    Stack,
    Select,
    MenuItem,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress
} from '@mui/material';
import AskAI from '../Images/AskAI.png';
import { getAskAISuggestion, multiSavePayrollItemDetailUpdate } from '../api';
import { useSnackbar } from 'notistack';
import { ITEM_TYPE } from '../Enums/Enum';

const DisabledSelect = ({ value, width = '100%' }) => (
    <Select
        size="small"
        disabled
        displayEmpty
        value={value}
        title={value}
        sx={{ width: width, height: 24, fontSize: "11px", backgroundColor: "#f5f5f5", "& .MuiSelect-select.Mui-disabled": { WebkitTextFillColor: "#000", padding: "0 6px", display: "flex", alignItems: "center" } }}
    >
        <MenuItem value={value} sx={{ fontSize: "11px" }}>{value || "--Select--"}</MenuItem>
    </Select>
);

const ASKAIpopup = ({ open, onClose, row, onApplySuccess, suggestionsData = [] }) => {
    const departmentValue = row?.departmentName || row?.department || '';
    const jobValue = row?.jobTitleName || row?.jobTitle || '';
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [isApplied, setIsApplied] = useState(false);
    const [suggestions, setSuggestions] = useState({ wages: [], addDeds: [], taxes: [] });
    const [autoMapItems, setAutoMapItems] = useState([]);
    const { enqueueSnackbar } = useSnackbar();

    // const hasFetchedRef = useRef(false);

    // useEffect(() => {
    //     console.log("ASKAIpopup Props:", { open, row });
    //     // if (open && !hasFetchedRef.current) {
    //     //     hasFetchedRef.current = true;
    //     //     fetchSuggestions();
    //     // }

    //     if (!open) {
    //         hasFetchedRef.current = false;
    //     }
    // }, [open]);

    useEffect(() => {
        if (!open) {
            setSuggestions({ wages: [], addDeds: [], taxes: [] });
            setAutoMapItems([]);
            return;
        }
        if (!suggestionsData.length) return;

        const wages = [];
        const addDeds = [];
        const taxes = [];
        const autoMap = [];

        const allowedSources = [4, 5, 6, 7];
        const autoMapSources = [1, 2, 3];

        suggestionsData.forEach(item => {
            const source = Number(item.ClassificationSource);

            if (allowedSources.includes(source)) {
                const type = Number(item.ItemType);

                if (item.TypeClassification === "Wages" || type === ITEM_TYPE.Wages || type === 8 || type === 0) {
                    const isWageClass7 = Number(item.ClassificationSource) === 7;
                    wages.push(isWageClass7 ? { ...item, AccountID: "", AccountName: "" } : item);
                } else if (type === ITEM_TYPE.Additions || type === ITEM_TYPE.Deductions) {
                    addDeds.push(item);
                } else if (type === ITEM_TYPE.EmployeeTaxes || type === ITEM_TYPE.EmployerTaxes) {
                    taxes.push(item);
                }
            } else if (autoMapSources.includes(source)) {
                autoMap.push(item);
            }
        });

        setSuggestions({ wages, addDeds, taxes });
        setAutoMapItems(autoMap);

    }, [open, suggestionsData]);

    const getAskAiPayload = () => {
        return {
            CorpID: row?.CorpID || "",
            PayrollEmpID: String(row?.PayrollEmpID || ""),
            DepartmentID: row?.DeptID || "",
            JobID: row?.JobID || "",
            DepartmentName: departmentValue,
            JobName: jobValue,
            IsDeptJob: true
        };
    };

    // eslint-disable-next-line no-unused-vars
    const fetchSuggestions = async () => {
        setLoading(true);
        try {
            const payload = getAskAiPayload();
            // console.log("API Request Payload:", payload);
            //console.log("CALL #3 - popup");
            const response = await getAskAISuggestion(payload);
            // console.log("API Response:", response);

            if (response) {
                const wages = [];
                const addDeds = [];
                const taxes = [];
                const autoMap = [];
                const dataList = response.ASKAISuggestion || (Array.isArray(response) ? response : []);

                const allowedSources = [4, 5, 6, 7];
                const autoMapSources = [1, 2, 3];
                dataList.forEach(item => {
                    const source = Number(item.ClassificationSource);
                    if (allowedSources.includes(source)) {
                        const type = Number(item.ItemType);
                        if (item.TypeClassification === "Wages" || type === ITEM_TYPE.Wages || type === 8 || type === 0) {
                            const isWageClass7 = Number(item.ClassificationSource) === 7;
                            wages.push(isWageClass7 ? { ...item, AccountID: "", AccountName: "" } : item);
                        } else if (type === ITEM_TYPE.Additions || type === ITEM_TYPE.Deductions) {
                            addDeds.push(item);
                        } else if (type === ITEM_TYPE.EmployeeTaxes || type === ITEM_TYPE.EmployerTaxes) {
                            taxes.push(item);
                        }
                    } else if (autoMapSources.includes(source)) {
                        autoMap.push(item);
                    }
                });
                setSuggestions({ wages, addDeds, taxes });

                if (autoMap.length > 0) {
                    setAutoMapItems(autoMap);
                }
            }
        } catch (error) {
            console.error("Error fetching AI suggestions:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (onClose) {
            onClose(false);
        }
    };

    const handleApply = async () => {
        setActionLoading(true);
        try {
            const allSuggestions = [
                ...suggestions.wages,
                ...suggestions.addDeds,
                ...suggestions.taxes,
                ...autoMapItems
            ];

            const itemsToApply = suggestionsData && suggestionsData.length > 0 ? suggestionsData : allSuggestions;

            // Apply selected classifications: Send only records with ClassificationSource 4, 6, and 7 to the API.
            // Records with 1, 2, or 4 are strictly excluded from the API request payload.
            // Exclude records that have no ItemType, an empty string, or an ItemType of 0
            const apiItems = allSuggestions.filter(item => {
                const source = Number(item.ClassificationSource);
                const rawType = item.ItemType;
                const isInvalidType = rawType === null || rawType === undefined || String(rawType).trim() === "" || Number(rawType) === 0;
                return [4, 6, 7].includes(source) && !isInvalidType;
            });

            const formattedItems = apiItems.map(item => ({
                Type: Number(item.ItemType),
                ImportedItemName: item.ImportedItemName || "",
                ItemName: item.ItemName || "",
                AccountID: item.AccountID || "",
                AccountName: item.AccountName || "",
                LiabilityAccountID: item.LiabilityAccountID || "",
                LiabilityAccountName: item.LiabilityAccountName || "",
                PayType: item.ClassifiedPayType || ""
            }));

            const payload = {
                CorporationID: row?.CorpID || "",
                CorporationName: String(row?.CorpName || ""),
                PayrollEmployeeID: String(row?.PayrollEmpID || ""),
                DepartmentID: row?.DeptID || "",
                DepartmentName: departmentValue,
                JobID: row?.JobID || "",
                JobName: jobValue,
                MultiSavePayrollItems: formattedItems
            };
            // console.log("multiSavePayrollItemDetailUpdate Request Payload:", payload);

            if (formattedItems.length > 0 && typeof multiSavePayrollItemDetailUpdate === "function") {
                const response = await multiSavePayrollItemDetailUpdate(payload);
                if (response?.StatusCode === 200 || response?.Status === "Success" || response === true) {
                    setIsApplied(true);

                    const askAiPayload = getAskAiPayload();
                    let freshSuggestions = itemsToApply;
                    try {
                        const aiResponse = await getAskAISuggestion(askAiPayload);
                        const dataList = aiResponse?.ASKAISuggestion || (Array.isArray(aiResponse) ? aiResponse : []);
                        if (dataList && dataList.length > 0) {
                            freshSuggestions = dataList;
                        }
                    } catch (aiErr) {
                        console.error("Error fetching AI suggestions after multi-save:", aiErr);
                    }

                    if (onApplySuccess) await onApplySuccess(freshSuggestions, true);
                } else {
                    // console.error("Failed to map API response:", response);
                    enqueueSnackbar(response?.Message || response?.Status || "Mapping failed", { variant: "error" });
                }
            } else {
                setIsApplied(true);
                if (onApplySuccess) await onApplySuccess(itemsToApply, true);
            }
        } catch (error) {
            // console.error("Apply error:", error);
            enqueueSnackbar(error?.message || "Mapping failed", { variant: "error" });
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth PaperProps={{ sx: { minHeight: "90vh", maxHeight: "90vh" } }}>
            <Toolbar sx={{ backgroundColor: "#F8F9FA", minHeight: '40px !important', px: 2, display: 'flex', gap: 2 }}>
                <Stack direction="row" alignItems="center" gap={1} sx={{ flex: 1 }}>
                    <img src={AskAI} alt="Ask AI" style={{ width: 18, height: 18 }} />
                    <Typography fontWeight={600} fontSize="14px">
                        AI Suggested
                    </Typography>
                </Stack>

                <Stack direction="row" alignItems="center" gap={1}>
                    <Typography fontSize={12} color="text.secondary" fontWeight={500}>Department:</Typography>
                    <DisabledSelect value={departmentValue} width={140} />
                </Stack>

                <Stack direction="row" alignItems="center" gap={1}>
                    <Typography fontSize={12} color="text.secondary" fontWeight={500}>Job:</Typography>
                    <DisabledSelect value={jobValue} width={140} />
                </Stack>
                {/* 
                <Button
                    variant="contained"
                    onClick={fetchSuggestions}
                    disabled={loading}
                    startIcon={<img src={AskAI} alt="Ask AI" style={{ width: 14, height: 14 }} />}
                    sx={{
                        ml: 1,
                        backgroundColor: "#b5dcf2",
                        color: "#000",
                        textTransform: "none",
                        fontSize: "11px",
                        "&:hover": { backgroundColor: "#a7d2ea" }
                    }}
                >
                    Ask AI
                </Button> */}

                {/* <IconButton
                    size="small"
                    edge="start"
                    onClick={onClose}
                    style={{ backgroundColor: "#686868", borderRadius: 2, width: 20, height: 20, marginLeft: 8 }}
                    disableRipple
                >
                    <CloseIcon sx={{ color: "white", fontSize: "11px" }} />
                </IconButton> */}
            </Toolbar>
            <DialogContent sx={{ display: 'flex', flexDirection: 'row', gap: 2, p: 2, overflow: 'hidden' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '85%', height: 300 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ mt: 0, display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0, overflowY: "auto", pr: 1, maxHeight: "calc(90vh - 100px)" }}>
                        {suggestions.wages.length > 0 && (
                            <TableContainer sx={{ border: "1px solid #e0e0e0", borderRadius: 2, flexShrink: 0, overflowX: "auto" }}>
                                <Table size="small" sx={{ minWidth: 400 }}>
                                    <TableHead>
                                        <TableRow sx={{ fontSize: "11px", fontWeight: 500, whiteSpace: "nowrap", height: '35px' }}>
                                            <TableCell sx={{ backgroundColor: "#f6e0c2", width: "40%", color: "#b8741a !important" }}>Wage</TableCell>
                                            <TableCell sx={{ backgroundColor: "#f6e0c2", width: "30%", color: "#b8741a !important" }}>Mapped to</TableCell>
                                            <TableCell sx={{ backgroundColor: "#f2f2f2", width: "30%" }}>COA</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {suggestions.wages.map((item, index) => (
                                            <TableRow key={`wage-${index}`} sx={{ height: '35px' }}>
                                                <TableCell sx={{ fontSize: 11, py: 0.5 }} title={item.ImportedItemName || ""}>{item.ImportedItemName || ""}</TableCell>
                                                <TableCell sx={{ py: 0.5 }}><DisabledSelect value={item.ItemName || ""} /></TableCell>
                                                <TableCell sx={{ py: 0.5 }}><DisabledSelect value={item.AccountName || item.AccountID || ""} /></TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {suggestions.addDeds.length > 0 && (
                            <TableContainer sx={{ border: "1px solid #e0e0e0", borderRadius: 2, flexShrink: 0, overflowX: "auto" }}>
                                <Table size="small" sx={{ minWidth: 400 }}>
                                    <TableHead>
                                        <TableRow sx={{ fontSize: "11px", fontWeight: 500, whiteSpace: "nowrap", height: '35px' }}>
                                            <TableCell sx={{ backgroundColor: "#f6e0c2", width: "25%", color: "#b8741a !important" }}>Additions/Deduction</TableCell>
                                            <TableCell sx={{ backgroundColor: "#f6e0c2", width: "25%", color: "#b8741a !important" }}>Item</TableCell>
                                            <TableCell sx={{ backgroundColor: "#f6e0c2", width: "25%", color: "#b8741a !important" }}>Mapped to</TableCell>
                                            <TableCell sx={{ backgroundColor: "#f2f2f2", width: "25%" }}>COA</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {suggestions.addDeds.map((item, index) => (
                                            <TableRow key={`addDed-${index}`} sx={{ height: '35px' }}>
                                                <TableCell sx={{ py: 0.5 }}><DisabledSelect value={Number(item.ItemType) === 9 ? "Addition" : "Deduction"} /></TableCell>
                                                <TableCell sx={{ fontSize: 11, py: 0.5 }} title={item.ImportedItemName || ""}>{item.ImportedItemName || ""}</TableCell>
                                                <TableCell sx={{ py: 0.5 }}><DisabledSelect value={item.ItemName || ""} /></TableCell>
                                                <TableCell sx={{ py: 0.5 }}><DisabledSelect value={item.AccountName || item.AccountID || ""} /></TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {suggestions.taxes.length > 0 && (
                            <TableContainer sx={{ border: "1px solid #e0e0e0", borderRadius: 2, flexShrink: 0, overflowX: "auto", maxHeight: 280 }}>
                                <Table size="small" stickyHeader sx={{ minWidth: 400 }}>
                                    <TableHead>
                                        <TableRow sx={{ fontSize: "11px", fontWeight: 500, whiteSpace: "nowrap", height: '35px' }}>
                                            <TableCell sx={{ backgroundColor: "#f6e0c2 !important", width: "20%", color: "#b8741a !important" }}>EE & ER Taxes</TableCell>
                                            <TableCell sx={{ backgroundColor: "#f6e0c2 !important", width: "20%", color: "#b8741a !important" }}>Item</TableCell>
                                            <TableCell sx={{ backgroundColor: "#f6e0c2 !important", width: "20%", color: "#b8741a !important" }}>Mapped to</TableCell>
                                            <TableCell sx={{ backgroundColor: "#f2f2f2 !important", width: "20%" }}>Expense Account</TableCell>
                                            <TableCell sx={{ backgroundColor: "#f2f2f2 !important", width: "20%" }}>Liability Account</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {suggestions.taxes.map((item, index) => (
                                            <TableRow key={`tax-${index}`} sx={{ height: '35px' }}>
                                                <TableCell sx={{ py: 0.5 }}><DisabledSelect value={Number(item.ItemType) === 11 ? "EE Tax" : (Number(item.ItemType) === 12 ? "ER Tax" : "Tax")} /></TableCell>
                                                <TableCell sx={{ fontSize: 11, py: 0.5 }} title={item.ImportedItemName || ""}>{item.ImportedItemName || ""}</TableCell>
                                                <TableCell sx={{ py: 0.5 }}><DisabledSelect value={item.ItemName || ""} /></TableCell>
                                                <TableCell sx={{ py: 0.5 }}><DisabledSelect value={item.AccountName || item.AccountID || ""} /></TableCell>
                                                <TableCell sx={{ py: 0.5 }}>
                                                    {Number(item.ItemType) === 12 && (
                                                        <DisabledSelect value={item.LiabilityAccountName || item.LiabilityAccountID || ""} />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {suggestions.wages.length === 0 && suggestions.addDeds.length === 0 && suggestions.taxes.length === 0 && (
                            <Typography sx={{ textAlign: "center", color: "text.secondary", mt: 4, fontSize: 12 }}>
                                No AI suggestions available.
                            </Typography>
                        )}
                    </Box>
                )}
                <Stack spacing={1.5} sx={{ width: 90, flexShrink: 0, position: "sticky", top: 0, alignSelf: "flex-start", pb: 3 }}>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={handleClose}
                        sx={{
                            backgroundColor: "#d32f2f",
                            color: "white",
                            textTransform: "none",
                            fontSize: "11px",
                            "&:hover": { backgroundColor: "#b71c1c" }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        disabled={isApplied || actionLoading}
                        onClick={handleApply}
                        sx={{
                            backgroundColor: "#b5dcf2",
                            color: "#000",
                            textTransform: "none",
                            fontSize: "11px",
                            "&:hover": { backgroundColor: "#a7d2ea" }
                        }}
                    >
                        {actionLoading ? <CircularProgress size={16} color="inherit" /> : "Apply"}
                    </Button>
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

export default ASKAIpopup;